import { Descriptions, message, Skeleton, Space } from 'antd';
import { useState } from 'react';

import { Alert, Drawer, Title } from '../../../../components/index';
import { GenericDrawerHeader } from '../../../../components/common/GenericDrawerHeader/GenericDrawerHeader';
import {
  CargoSuppressionItemType,
  useDeleteCargoSuppressionInternalMutation,
  useGetCargoSuppressionListByDockingQuery,
  useGetCargoSuppressionPrefillQuery,
} from '../../../../services/cargoSuppressionApi';
import { CargoSuppressionForm } from './CargoSuppressionForm';
import { CargoSuppressionsTable } from './CargoSuppressionsTable';

const { Item: DescItem } = Descriptions;

type CargoSuppressionDrawerProps = {
  visible: boolean;
  onClose: () => void;
  dockingCode: string;
};

export function CargoSuppressionDrawer(props: CargoSuppressionDrawerProps) {
  const { visible, onClose, dockingCode } = props;

  const [editingItem, setEditingItem] =
    useState<CargoSuppressionItemType | null>(null);

  const skip = !visible || !dockingCode;

  const { data: prefill, isFetching: isLoadingPrefill } =
    useGetCargoSuppressionPrefillQuery({ docking_code: dockingCode }, { skip });

  const { data: listData, isFetching: isLoadingList } =
    useGetCargoSuppressionListByDockingQuery(
      { docking_code: dockingCode },
      { skip }
    );

  const [deleteSuppression] = useDeleteCargoSuppressionInternalMutation();

  async function handleDelete(item: CargoSuppressionItemType) {
    const res = await deleteSuppression({
      docking_code: dockingCode,
      suppression_id: item.id,
    });
    if ('data' in res) {
      message.success('Supressão removida');
    }
  }

  const blockingErrors = prefill?.preconditions_errors || [];

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      width={1000}
      destroyOnClose
      closable={false}
      bodyStyle={{ padding: '24px 32px' }}
    >
      <GenericDrawerHeader
        showBackButton
        onBack={onClose}
        title={
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            Supressão de Carga — Atracação {dockingCode}
          </span>
        }
      />
      {isLoadingPrefill ? (
        <Skeleton active />
      ) : (
        <Space
          direction="vertical"
          size={16}
          style={{ width: '100%', marginTop: 16 }}
        >
          {blockingErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Não é possível registrar supressões para esta atracação"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                  {blockingErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              }
            />
          )}

          {prefill && (
            <Descriptions size="small" column={2} bordered>
              <DescItem label="Atracação">
                {prefill.docking_code || '-'}
              </DescItem>
              <DescItem label="Ano">{prefill.docking_year ?? '-'}</DescItem>
              <DescItem label="Berço">
                {prefill.docking_berth_code || '-'}
              </DescItem>
              <DescItem label="Local ANTAQ">
                {prefill.local_atracacao || '-'}
              </DescItem>
            </Descriptions>
          )}

          <div>
            <Title>Supressões registradas</Title>
            <CargoSuppressionsTable
              data={listData?.results || []}
              loading={isLoadingList}
              onEdit={setEditingItem}
              onDelete={handleDelete}
            />
          </div>

          {blockingErrors.length === 0 && (
            <div>
              <Title>
                {editingItem ? 'Editar supressão' : 'Nova supressão'}
              </Title>
              <CargoSuppressionForm
                dockingCode={dockingCode}
                products={prefill?.products || []}
                generalCargoErrors={
                  prefill?.general_cargo_preconditions_errors || []
                }
                containerOptions={prefill?.container_options || []}
                hasContainerOperation={
                  prefill?.has_container_operation ?? false
                }
                hasGeneralCargoOperation={
                  prefill?.has_general_cargo_operation ?? false
                }
                editingItem={editingItem}
                onSaved={() => setEditingItem(null)}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          )}
        </Space>
      )}
    </Drawer>
  );
}
